import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Hourglass, AlertTriangle, X } from 'lucide-react';

/**
 * App-shell banner surfacing org subscription state.
 *
 * Reads `state.session.expiry` (populated from /api/auth/session). Three states:
 *   - effectiveMode='none'         → no banner
 *   - effectiveMode='informational'→ amber banner with days-remaining (dismissible per-session)
 *   - effectiveMode='soft'         → rose full-width banner, non-dismissible
 *   - effectiveMode='hard'         → would never reach here (login is blocked)
 *
 * Sultans see the banner too if they're impersonating; otherwise they skip
 * (they manage expiry from SultanExpiryPage anyway).
 */
export default function ExpiryBanner() {
  const expiry = useSelector((s) => s.session.expiry);
  const isSultan = useSelector((s) => s.sultan?.isSultan);
  const sidebarMode = useSelector((s) => s.ui?.sidebarMode);
  const [dismissed, setDismissed] = useState(false);

  // Sultans in platform mode are managing expiry centrally — don't nag them.
  if (isSultan && sidebarMode === 'platform') return null;
  if (!expiry || !expiry.effectiveMode || expiry.effectiveMode === 'none') return null;

  const days = expiry.expiresAt
    ? Math.round((new Date(expiry.expiresAt).getTime() - Date.now()) / 86_400_000)
    : null;

  if (expiry.effectiveMode === 'informational') {
    if (dismissed) return null;
    return (
      <div className="bg-amber-50 dark:bg-amber-500/10 border-b border-amber-200 dark:border-amber-500/30 px-4 py-2 flex items-center gap-2 text-xs">
        <Hourglass size={14} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
        <span className="text-amber-800 dark:text-amber-200">
          Your subscription expires in <strong className="tabular-nums">{days} day{days === 1 ? '' : 's'}</strong>. Contact your platform administrator to renew.
        </span>
        <Link to="/settings/org/expiry" className="ms-auto text-amber-700 dark:text-amber-300 underline hover:no-underline font-semibold">View details</Link>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="ms-2 text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200"
          title="Dismiss for this session"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  // soft: account is past expiry but inside grace window. Non-dismissible.
  return (
    <div className="bg-rose-50 dark:bg-rose-500/10 border-b border-rose-200 dark:border-rose-500/30 px-4 py-2.5 flex items-center gap-2 text-xs">
      <AlertTriangle size={14} className="text-rose-600 dark:text-rose-400 flex-shrink-0" />
      <span className="text-rose-800 dark:text-rose-200">
        Subscription expired{days !== null && days < 0 ? ` ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago` : ''}. Some features are restricted — your account will be locked when the grace period ends.
      </span>
      <Link to="/settings/org/expiry" className="ms-auto text-rose-700 dark:text-rose-300 underline hover:no-underline font-semibold">Renewal info</Link>
    </div>
  );
}
